import { IconButton, List, ListItem, ListItemButton, ListItemText, Paper, Popper } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { FilterSubMenuItem } from "./FilterMenuForButtonGridFilter";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import menuStyles from "./filterMenuStyles.module.css";
import clsx from "clsx";
import CheckBoxMultiCheck from "../../../Sections/ElementsSections/CheckBoxMultiCheck";
import { isEmptyObject } from "../../../Tools/Tools";

interface Props {
  values: Array<FilterSubMenuItem>;
  onClickMenuItem: (ev: any, command?: string, item?: { [key: string]: FilterSubMenuItem }) => Promise<void> | void;
  name: string;
  disabled?: boolean;
}

/**
 * Возвращает подменю для меню снятия фильтрации с колонок
 *
 * Поля компонента:
 *    1.[open, setOpen] - состояние открытия подменю
 *    2.[timer, setTimer] - задержка открытия подменю
 *    3.[checkStates, setCheckStates] - объект с множественным выбором элементов
 *    4.anchorEl - якорь для подменю
 *
 * @param {Object} [props]
 * @param {Object} [props.values] массив с данными для элементов подменю
 * @param {Object} [props.onClickMenuItem] функция клика на элемент подменю
 * @param {Object} [props.name] название для элемента с подменю
 * @returns возвращает элемент с подменю
 */
export function MenuColumnsFilters(props: Props): React.JSX.Element {
  const { values, onClickMenuItem, name, disabled } = props;

  const [open, setOpen] = useState(false);
  const [timer, setTimer] = useState<NodeJS.Timeout>();
  const [checkStates, setCheckStates] = useState<{ [key: string]: FilterSubMenuItem }>({});

  const anchorEl = useRef(null);

  /**
   * Автозакрытие подменю при отсутствии элементов
   */
  const updateOpenState = () => {
    if (!values || !values.length) {
      setOpen(false);
    }
  };
  useEffect(updateOpenState, [values]);

  /**
   * При наведении открывает меню с задержкой
   */
  const onMouseEnter = () => {
    if (!values || !values.length) return;
    const timout = setTimeout(() => {
      setOpen(true);
    }, 350);
    setTimer(timout);
  };

  /**
   * При выходе курсора мыши с элемента сбрасывает таймер и закрывает подменю если оно открыто
   */
  const onMouseLeave = () => {
    if (!values || !values.length) return;
    clearTimeout(timer);
    setOpen(false);
  };

  /**
   * Отмечает элемент для снятия или удаляет его из объекта
   *
   * @param data данные от чек-бокса
   * @param item данные элемента
   */
  const onCheck = (data: { CheckState: "0" | "1" }, item: FilterSubMenuItem) => {
    if (!item.key) return;
    if (data.CheckState === "1") {
      checkStates[item.key] = item;
    } else delete checkStates[item.key];

    setCheckStates({ ...checkStates });
  };

  return (
    <div
      className={clsx(menuStyles.nestedMenu, disabled && menuStyles.nestedMenuDisabled)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      ref={anchorEl}
    >
      <ListItemText className={clsx(menuStyles.nestedMenuItemLabel)}>{name}</ListItemText>
      <ChevronRightIcon />
      <Popper className={clsx(menuStyles.nestedMenuPopper)} open={open} placement="right" anchorEl={anchorEl.current}>
        <Paper>
          <IconButton
            onClick={(ev) => {
              onClickMenuItem(ev, "CFilterClear", checkStates);
            }}
            disabled={isEmptyObject(checkStates)}
            className={clsx(menuStyles.nestedMenuButton)}
          >
            Снять выбранные
          </IconButton>
          <List>
            {values?.map((item: FilterSubMenuItem, index) => {
              const { value, name, key } = item;
              return (
                <ListItem className={clsx(menuStyles.nestedMenuItem)} key={"ButtonsMenu" + index}>
                  <div className={clsx(menuStyles.nestedMenuCheck)}>
                    <CheckBoxMultiCheck
                      CheckState={key && checkStates[key] ? "1" : "0"}
                      onEdit={(data: { CheckState: "0" | "1" }) => onCheck(data, item)}
                    />
                  </div>
                  <ListItemButton
                    onClick={(ev) => {
                      key && onClickMenuItem(ev, value, { [key]: item });
                    }}
                    className={clsx(menuStyles.nestedMenuItemLabel)}
                  >
                    <ListItemText>{name}</ListItemText>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      </Popper>
    </div>
  );
}
